/**
 * PlatformAuthHealthPanel — Founder-only "Authentication Health" tile.
 *
 * Pulls from GET /api/platform/auth-health. Rendered inside
 * PlatformAdmin.jsx below the existing analytics.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, RefreshCw, LogIn, Users2, Activity,
  Loader2,
} from "lucide-react";

const REASON_LABEL = {
  bad_password:     "Wrong password",
  user_not_found:   "Unknown email",
  expired:          "Session expired",
  invalid_signature:"Bad signature",
  invalid_token:    "Malformed token",
  missing:          "Missing token",
};

export default function PlatformAuthHealthPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/platform/auth-health?days=${days}`);
      setData(data);
    } catch (e) {
      toast.error(apiError(e, "Failed to load auth health"));
    } finally {
      setLoading(false);
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 60 seconds while tab is visible.
  useEffect(() => {
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const chartMax = data?.hourly_last_24h
    ? Math.max(...data.hourly_last_24h.map((h) =>
        h.login_ok + h.login_fail + h.auth_fail), 1)
    : 1;

  return (
    <section
      className="card-widget mt-10"
      data-testid="platform-auth-health"
    >
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-pine" />
          <div className="overline text-ink-soft">Authentication Health</div>
        </div>
        <div className="inline-flex items-center gap-3">
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-ink-soft" />}
          <div className="inline-flex rounded-full border border-[#E8EAE9] p-1 bg-white text-xs">
            {[1, 7, 30].map((d) => (
              <button
                key={d}
                data-testid={`auth-health-range-${d}`}
                onClick={() => setDays(d)}
                className={`px-3 py-1 rounded-full transition ${
                  days === d ? "bg-pine text-linen" : "text-ink-mid hover:text-ink"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {!data ? (
        <div className="text-sm text-ink-soft">Loading…</div>
      ) : (
        <>
          {/* Top-line metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <HealthPill
              icon={LogIn}
              label="Login success"
              value={`${data.login_success_pct}%`}
              subtext={`${data.login_ok}/${data.login_total} logins`}
              tone={data.login_success_pct >= 80 ? "good" : "warn"}
              testid="auth-stat-success"
            />
            <HealthPill
              icon={RefreshCw}
              label="Sessions refreshed"
              value={data.refresh_ok}
              subtext="incl. sliding renewals"
              testid="auth-stat-refreshes"
            />
            <HealthPill
              icon={ShieldAlert}
              label="Auth failures"
              value={data.auth_fail + data.login_fail}
              subtext={`${data.auth_fail} 401s · ${data.login_fail} bad logins`}
              tone={(data.auth_fail + data.login_fail) === 0 ? "good" : "warn"}
              testid="auth-stat-failures"
            />
            <HealthPill
              icon={Users2}
              label="Active users · 24h"
              value={data.active_users_24h}
              subtext="distinct sessions"
              testid="auth-stat-active"
            />
          </div>

          {/* Top failure reasons */}
          {data.top_failure_reasons?.length > 0 && (
            <div className="mt-6">
              <div className="overline text-ink-soft mb-2">Top failure reasons</div>
              <div className="space-y-2" data-testid="auth-top-reasons">
                {data.top_failure_reasons.map((r) => {
                  const totalFail = data.top_failure_reasons.reduce((s, x) => s + x.count, 0);
                  const pct = Math.round((r.count / totalFail) * 100);
                  return (
                    <div key={r.reason} data-testid={`auth-reason-${r.reason}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">
                          {REASON_LABEL[r.reason] || r.reason}
                          <span className="ml-2 text-[10px] uppercase tracking-widest text-ink-soft">
                            {r.reason}
                          </span>
                        </span>
                        <span className="text-ink-mid tabular-nums">
                          {r.count} · {pct}%
                        </span>
                      </div>
                      <div className="mt-1 h-2 bg-linen rounded-full overflow-hidden">
                        <div className="h-full bg-[#D06A42] transition-all"
                             style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Hourly trend chart — last 24h */}
          <div className="mt-6">
            <div className="overline text-ink-soft mb-2 flex items-center gap-1.5">
              <Activity className="w-3 h-3" /> Last 24h · hourly
            </div>
            <div className="flex items-end gap-1 h-24" data-testid="auth-hourly-chart">
              {data.hourly_last_24h.map((h) => {
                const total = h.login_ok + h.login_fail + h.auth_fail;
                const okH   = (h.login_ok / chartMax) * 100;
                const failH = ((h.login_fail + h.auth_fail) / chartMax) * 100;
                return (
                  <div key={h.hour} className="flex-1 flex flex-col justify-end"
                       title={`${h.hour}:00 · ${h.login_ok} ok, ${h.login_fail} bad login, ${h.auth_fail} 401`}>
                    {failH > 0 && (
                      <div className="bg-[#B94A48]"
                           style={{ height: `${Math.max(2, failH)}%` }} />
                    )}
                    {okH > 0 && (
                      <div className="bg-pine"
                           style={{ height: `${Math.max(2, okH)}%` }} />
                    )}
                    {total === 0 && <div className="bg-[#F1F2F0]" style={{ height: "2px" }} />}
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex gap-4 text-[10px] text-ink-mid">
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 bg-pine rounded-sm" /> Login OK
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 bg-[#B94A48] rounded-sm" /> Failures
              </span>
              <span className="ml-auto text-ink-soft">Hours 0–23 UTC</span>
            </div>
          </div>

          {/* Recent failure log */}
          {data.recent_failures?.length > 0 && (
            <div className="mt-6">
              <div className="overline text-ink-soft mb-2">Recent failures (last 20)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="auth-recent-table">
                  <thead>
                    <tr className="text-left text-ink-soft overline">
                      <th className="py-2 pr-4">When</th>
                      <th className="pr-4">Kind</th>
                      <th className="pr-4">Reason</th>
                      <th className="pr-4">Email</th>
                      <th className="pr-4">Path</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_failures.map((f, idx) => (
                      <tr key={idx} className="border-t border-[#F1F2F0]">
                        <td className="py-2 pr-4 text-ink-mid whitespace-nowrap">
                          {new Date(f.at).toLocaleString("en-IN", { hour: "numeric", minute: "2-digit", day: "numeric", month: "short" })}
                        </td>
                        <td className="pr-4">
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                            f.kind === "login_fail" ? "bg-[#FDF3ED] text-[#8A3F1E]" : "bg-[#FCECEC] text-[#B94A48]"}`}>
                            {f.kind.replace("_", " ")}
                          </span>
                        </td>
                        <td className="pr-4 font-mono">{f.reason || "—"}</td>
                        <td className="pr-4 text-ink-mid truncate max-w-[180px]">{f.email || "—"}</td>
                        <td className="pr-4 text-ink-mid font-mono truncate max-w-[160px]">{f.path || "—"}</td>
                        <td className="text-ink-soft font-mono">{f.ip || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-4 text-[11px] text-ink-soft text-center">
            Generated {new Date(data.generated_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })} · auto-refreshes every 60 s
          </div>
        </>
      )}
    </section>
  );
}

function HealthPill({ icon: Icon, label, value, subtext, tone, testid }) {
  const bg =
    tone === "warn" ? "bg-[#FCECEC] border-[#EDC7C6]" :
    tone === "good" ? "bg-[#E8F0EB] border-[#C7DDCF]" :
    "bg-white border-[#E8EAE9]";
  const numColor =
    tone === "warn" ? "text-[#B94A48]" :
    tone === "good" ? "text-[#2D6A4F]" :
    "text-ink";
  return (
    <div className={`rounded-2xl p-4 border ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-ink-soft">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`font-display text-3xl mt-1 tabular-nums ${numColor}`}>{value}</div>
      {subtext && (
        <div className="text-[11px] text-ink-soft mt-1">{subtext}</div>
      )}
    </div>
  );
}
